// src/MaintenanceHome.js
//
// Central landing for the /maintenance hub. Replaces the older "tour" page
// with an operations-focused dashboard:
//   • Bigger date stripe so it doubles as a wall display.
//   • Four actionable pills at the top (jobs today, units expected, filters
//     to swap this week, upcoming starts in the next 30 days).
//   • "Today's appointments" list: every contract starting/ending today,
//     with a [View →] button that opens the manager's portal for that
//     building in a new tab (uses ?buildingId=<id>&preview=1).
//   • Right column: "Upcoming maintenances" — the next 8 contract windows
//     across all buildings sorted by how soon they start, with each row
//     showing days-until-start, filter type/size, and any building-level
//     notes.
//
// Backend: still backed by /api/maintenance-schedule/buildings and
// /api/getTechStartTimes (no new endpoints needed beyond filter/notes/
// manager fields surfaced on the buildings response).

import './InternalMobile.css';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMaintenanceAccess } from './MaintenanceAccessContext';
import {
  FaCalendarAlt,
  FaChevronRight,
  FaBuilding,
  FaCheckCircle,
  FaBolt,
  FaExternalLinkAlt,
  FaUsers,
  FaFilter,
  FaStickyNote,
  FaClock,
  FaChartLine,
  FaTachometerAlt,
  FaCheckSquare,
  FaPen,
  FaCloudUploadAlt,
  FaFilePdf,
  FaFileExcel,
  FaTimes,
  FaSpinner,
} from 'react-icons/fa';
// IMPORTANT: import the shared API base from src/config/api.js — it forces
// production builds to use Heroku and ignores `.env` values like
// `http://localhost:5051` that are only meant for `npm start`. Don't read
// process.env.REACT_APP_API_URL directly here or production will try to
// fetch from the developer's laptop.
import API_BASE_URL from './config/api';
// Re-use the cycle-detail modal the Calendar / Schedule tabs already
// render, so clicking an "Upcoming maintenances" row here surfaces the
// exact same dates / daily schedule / filter info / special notes
// instead of jumping the user to another page first.
import { MaintenanceModal } from './MaintenanceSchedule';
import { maintenanceScopeLabel } from './utils/maintenanceScope';

const TARGET_MIN = 9 * 60 + 15;

// Shared palette — kept aligned with ManageAccounts so the hub feels cohesive.
const C = {
  navy: '#1F3A68',
  navySoft: '#2C4E84',
  ink: '#1a2236',
  inkSoft: '#374151',
  muted: '#6b7280',
  line: '#E6E8EC',
  lineSoft: '#EFF2F7',
  bg: '#F4F7FB',
  card: '#ffffff',
  green: '#1c9550',
  greenSoft: '#e3f5ea',
  blue: '#2563eb',
  blueSoft: '#eff6ff',
  blueBorder: '#bfdbfe',
  blueInk: '#1e40af',
  amber: '#C68A1C',
  amberSoft: '#fdf3df',
  amberBorder: '#fcd34d',
  amberInk: '#92400e',
  red: '#B62525',
  redSoft: '#fbe2e2',
  redBorder: '#fecaca',
  redInk: '#991b1b',
  purple: '#7c3aed',
  shadow: '0 1px 3px rgba(20,30,60,.06), 0 8px 24px rgba(20,30,60,.04)',
};

const sharedFont =
  '-apple-system, BlinkMacSystemFont, "Segoe UI", "Inter", "Helvetica Neue", Arial, sans-serif';

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

const todayISO = () => {
  const d = new Date();
  const tz = d.getTimezoneOffset() * 60000;
  return new Date(d - tz).toISOString().slice(0, 10);
};

function getContractTypeColors(t) {
  const type = String(t || '').toLowerCase();
  if (type.includes('hvac') || type.includes('fancoil') || type.includes('fan-coil') || type.includes('heat-pump')) return { fg: C.purple, bg: '#f5f3ff' };
  if (type.includes('dryer')) return { fg: C.amber, bg: C.amberSoft };
  if (type.includes('water')) return { fg: '#0891b2', bg: '#ecfeff' };
  if (type.includes('mech')) return { fg: '#0e7490', bg: '#cffafe' };
  if (type.includes('valve')) return { fg: C.green, bg: C.greenSoft };
  if (type.includes('stack')) return { fg: '#9333ea', bg: '#faf5ff' };
  return { fg: C.inkSoft, bg: '#f3f4f6' };
}

function fmtRange(start, end) {
  const toLabel = (s) => {
    if (!s) return '';
    const [y, m, d] = s.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };
  if (!start && !end) return '—';
  if (!end || start === end) return toLabel(start);
  return `${toLabel(start)} → ${toLabel(end)}`;
}

function fmtDaysUntil(n) {
  if (n == null) return '—';
  if (n === 0) return 'Today';
  if (n === 1) return 'Tomorrow';
  if (n > 0) return `In ${n} day${n === 1 ? '' : 's'}`;
  return `${Math.abs(n)} day${Math.abs(n) === 1 ? '' : 's'} ago`;
}

function openBuildingPreview(buildingId) {
  if (!buildingId) return;
  const url = `${window.location.origin}/?buildingId=${encodeURIComponent(buildingId)}&preview=1`;
  window.open(url, '_blank', 'noopener,noreferrer');
}

// Figure out which file column on MaintenanceInspections this cycle should
// upload into. Dryer-vent cycles get their own column (`dryerReport`); every
// other in-suite or fancoil-style cycle goes into the canonical `finalReport`
// column.
//
// Use the canonical `Type` field from MaintenanceInspections (lowercased
// — values are "hvac" or "dryer") as the SOLE source of truth. Earlier
// versions of this function fell back to a substring scan of
// `maintenanceType` (which is the human-readable equipment list, e.g.
// "heatpump, erv, dryer" for a building whose in-suite contract covers a
// heat pump, ERV, AND dryer vent). That string contains "dryer" for HVAC
// rows whose equipment happens to include dryers, so an in-suite HVAC
// final report got mis-routed to `dryerReport` — invisible in the
// manager dashboard.
function reportFieldForCycle(inspection, building) {
  const canonical = String(inspection?.type || '').trim().toLowerCase();
  if (canonical === 'dryer') return 'dryerReport';
  if (canonical === 'hvac') return 'finalReport';
  // Last-resort fallback for older rows missing the `Type` column: lean
  // on the contract-level type. Only match the exact slug "dryer-vent"
  // — never substring-match the equipment list.
  const contract = String(building?.contractType || '').trim().toLowerCase();
  if (contract === 'dryer-vent' || contract === 'dryer') return 'dryerReport';
  return 'finalReport';
}

// Build the schedule-edit URL: same target the technician uses (FieldBuildingRun),
// pre-loaded to the specific cycle so the back office can fix dates / cycle / notes.
function buildEditUrl(buildingObjectId, maintenanceID) {
  if (!buildingObjectId) return null;
  const base = `/schedule/buildings/${encodeURIComponent(buildingObjectId)}`;
  return maintenanceID ? `${base}?maintenanceID=${encodeURIComponent(maintenanceID)}` : base;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function MaintenanceHome() {
  const { canViewTab } = useMaintenanceAccess();
  const navigate = useNavigate();

  // --- data ---
  const [buildings, setBuildings] = useState([]);
  const [buildingsLoading, setBuildingsLoading] = useState(true);
  const [techToday, setTechToday] = useState(null);
  const [techTodayLoading, setTechTodayLoading] = useState(true);

  // Cycle-detail modal — opened by tapping a row in the "Upcoming
  // maintenances" list (and re-usable elsewhere when we need the same
  // detail card). Stores the {building, inspection} pair the modal needs.
  const [detailCtx, setDetailCtx] = useState(null);

  // Upload modal — opened by the "Complete" CTA on each appointment row.
  // We keep the entire { building, inspection, fieldName } context here so
  // the modal can render the building name in its header AND know which
  // MaintenanceInspections row + column to write to on submit.
  const [uploadCtx, setUploadCtx] = useState(null);
  // Set of inspection objectIds we've already completed in this session.
  // Used to mark appointment rows as Completed without a full refetch.
  const [completedInspIds, setCompletedInspIds] = useState(() => new Set());
  // Date the dashboard is "viewing". Defaults to today; the date picker
  // in the header lets the back office time-travel to past/future days
  // — every metric pill, appointment list, and upcoming list re-derives
  // off this date.
  const [selectedDate, setSelectedDate] = useState(() => todayISO());

  // Lifted into a function so the detail-modal's onSaved can call it
  // again after a successful edit, refreshing the buildings list (and
  // therefore the upcoming-maintenances rows) in place.
  const reloadBuildings = React.useCallback(async () => {
    setBuildingsLoading(true);
    try {
      const r = await fetch(`${API_BASE_URL}/api/maintenance-schedule/buildings`);
      const d = await r.json();
      setBuildings(Array.isArray(d.buildings) ? d.buildings : []);
    } catch (e) {
      console.error('[MaintenanceHome] buildings fetch failed', e);
      setBuildings([]);
    } finally {
      setBuildingsLoading(false);
    }
  }, []);

  useEffect(() => {
    reloadBuildings();
  }, [reloadBuildings]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setTechTodayLoading(true);
      try {
        // Pull the last 90 days in one shot — the response includes a
        // `daily_summary` array (one row per calendar date) that drives
        // both the sparklines underneath each pill AND today's pill values.
        // We filter out weekends client-side so the sparklines + baseline
        // only reflect working days.
        const url = `${API_BASE_URL}/api/getTechStartTimes?lookback_days=90&exclude_outliers=true`;
        const r = await fetch(url);
        const d = await r.json();
        if (!cancelled) setTechToday(d);
      } catch (e) {
        console.error('[MaintenanceHome] punctuality fetch failed', e);
        if (!cancelled) setTechToday(null);
      } finally {
        if (!cancelled) setTechTodayLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // --- derived ---
  const today = todayISO();
  const isViewingToday = selectedDate === today;

  // "Active Maintenances Today" — every inspection currently flagged
  // Active across all buildings, regardless of whether its window covers
  // the picker's selected date. The list is a single rolling backlog the
  // back office can chip through: the oldest startTerm bubbles to the
  // top so things scheduled weeks ago but never closed out get the most
  // attention. We dedupe on the inspection's objectId so each
  // MaintenanceInspections row gets its own line + its own Complete CTA
  // (each type needs its own report PDF on upload), and we hide rows
  // we've already marked complete in this session so the list shrinks
  // as the user works.
  const activeMaintenances = useMemo(() => {
    const out = [];
    const seen = new Set();
    buildings.forEach((b) => {
      (b.maintenanceHistory || []).forEach((h) => {
        if (!h || h.status !== 'Active') return;
        if (h.objectId && completedInspIds.has(h.objectId)) return;
        const k = h.objectId
          ? `o:${h.objectId}`
          : `b:${b.buildingId}|t:${h.maintenanceType || ''}|s:${h.startTerm || ''}|e:${h.endTerm || ''}`;
        if (seen.has(k)) return;
        seen.add(k);
        out.push({ building: b, inspection: h });
      });
    });
    // Oldest start date first; tie-break by building name so the order
    // stays stable across renders.
    return out.sort((a, b) => {
      const sa = a.inspection.startTerm || '9999-12-31';
      const sb = b.inspection.startTerm || '9999-12-31';
      if (sa !== sb) return sa < sb ? -1 : 1;
      return (a.building.name || '').localeCompare(b.building.name || '');
    });
  }, [buildings, completedInspIds]);
  // Backwards-compat alias so the existing JSX doesn't need renaming.
  const todaysInspections = activeMaintenances;

  // Upcoming (next 60d) contract windows — used by both the "Upcoming
  // maintenances" panel AND the headline pill. We walk maintenanceHistory
  // for every future window (not just the single `upcomingInspection` per
  // building), so a building scheduled for both fancoil + dryer at the same
  // time shows up as two rows. Dedupe is again on inspection.objectId so
  // rows sharing a maintenanceID across types both render.
  const upcomingMaintenances = useMemo(() => {
    const list = [];
    const seen = new Set();
    buildings.forEach((b) => {
      // Use maintenanceHistory (includes Active + Upcoming cycles, sorted),
      // plus the dedicated `upcomingInspection` as a safety net for backends
      // that only populate one.
      const sources = [];
      if (Array.isArray(b.maintenanceHistory)) sources.push(...b.maintenanceHistory);
      if (b.upcomingInspection) sources.push(b.upcomingInspection);

      sources.forEach((u) => {
        const start = u?.startTerm;
        if (!start) return;
        // Days-until is computed relative to the selected date, not the
        // wall clock, so the "Tomorrow / In 6 days" labels stay accurate
        // when the back office is reviewing a past or future date.
        const ref = new Date(`${selectedDate}T00:00:00`);
        const startDate = new Date(`${start}T00:00:00`);
        const days = Math.round((startDate - ref) / 86400000);
        // Skip already-started (selected date is in the "Appointments"
        // list above) and anything more than ~2 months out.
        if (days == null || days <= 0 || days > 60) return;
        const k = u.objectId
          ? `o:${u.objectId}`
          : `b:${b.buildingId}|t:${u.maintenanceType || ''}|s:${start}`;
        if (seen.has(k)) return;
        seen.add(k);
        list.push({ building: b, inspection: u, days });
      });
    });
    list.sort((a, b) => a.days - b.days || (a.building.name || '').localeCompare(b.building.name || ''));
    return list;
  }, [buildings, selectedDate]);

  // (Previously held jobsToday/needsScheduling pill data — replaced by the
  // 30-day sparkline metrics fetched from /api/getTechStartTimes.)

  // Today's-vs-30d metrics for the headline pills. We always pull a 30 day
  // window, then slice "today" from the `daily_summary` series. The series
  // itself (padded to 30 calendar days, missing dates filled with zero/null)
  // also feeds the sparkline drawn under each pill.
  const dailySummary = useMemo(() => Array.isArray(techToday?.daily_summary) ? techToday.daily_summary : [], [techToday]);

  // Build a date-keyed map of the API response, then pad 90 calendar days
  // back from today and DROP weekends. The pills compare today vs the 30
  // most-recent weekdays excluding today, while the sparklines plot all
  // 90 days of weekday data so you can see the trend in context.
  const series = useMemo(() => {
    const map = {};
    dailySummary.forEach((d) => { map[d.date] = d; });
    const out = [];
    const now = new Date();
    for (let i = 89; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() - i);
      const dow = d.getDay();
      if (dow === 0 || dow === 6) continue; // skip Sun (0) + Sat (6)
      const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      const row = map[iso];
      out.push({
        date: iso,
        jsDate: d,
        weekday_short: d.toLocaleDateString('en-US', { weekday: 'short' }),
        unit_count: row?.unit_count ?? 0,
        active_techs: row?.active_techs ?? 0,
        avg_first_min: row?.avg_first_min ?? null,
        avg_first_time: row?.avg_first_time ?? null,
        avg_gap_min: row?.avg_gap_min ?? null,
        avg_gap_display: row?.avg_gap_display ?? null,
      });
    }
    return out;
  }, [dailySummary]);

  // Row used by the metric pills — pulled from the FULL daily_summary map
  // (which includes weekends) so the back office can review any past or
  // future date without losing data on Sat/Sun. Falls back to a zeroed
  // row when the API has no entry for that date (e.g. far future).
  const allDaysMap = useMemo(() => {
    const m = {};
    dailySummary.forEach((d) => { m[d.date] = d; });
    return m;
  }, [dailySummary]);
  const selectedRow = useMemo(() => {
    const r = allDaysMap[selectedDate];
    if (r) return r;
    return {
      date: selectedDate,
      unit_count: 0,
      active_techs: 0,
      avg_first_min: null,
      avg_first_time: null,
      avg_gap_min: null,
      avg_gap_display: null,
    };
  }, [allDaysMap, selectedDate]);
  // Baseline = average of the most recent 30 WEEKDAYS excluding today.
  // (~22 calendar days but feels like "the last month of working days".)
  const baseline = useMemo(() => {
    const prior = series.slice(0, -1);
    const lastN = prior.slice(-30);
    const reducer = (key) => {
      const vals = lastN.map((r) => r[key]).filter((v) => v != null);
      if (!vals.length) return null;
      return vals.reduce((a, b) => a + b, 0) / vals.length;
    };
    return {
      unit_count: reducer('unit_count'),
      active_techs: reducer('active_techs'),
      avg_first_min: reducer('avg_first_min'),
      avg_gap_min: reducer('avg_gap_min'),
    };
  }, [series]);

  // Helpers to format a minutes-since-midnight value as 6:38 AM, and a
  // duration in minutes as "Xh Ym" / "Ym". Keeps the pill labels short.
  const fmt12h = (mins) => {
    if (mins == null) return null;
    let h = Math.floor(mins / 60);
    let m = Math.round(mins - h * 60);
    if (m === 60) { h += 1; m = 0; }
    const period = h < 12 ? 'AM' : 'PM';
    const h12 = h % 12 || 12;
    return `${h12}:${String(m).padStart(2, '0')} ${period}`;
  };
  const fmtMins = (mins) => {
    if (mins == null) return null;
    const x = Math.round(mins);
    if (x < 60) return `${x}m`;
    const h = Math.floor(x / 60);
    const m = x - h * 60;
    return m ? `${h}h ${m}m` : `${h}h`;
  };

  // Per-pill series. Each entry mirrors the `series` row so the Sparkline
  // tooltip can show date + formatted value without extra lookups.
  const seriesUnits = series.map((r) => ({ date: r.date, jsDate: r.jsDate, value: r.unit_count }));
  const seriesTechs = series.map((r) => ({ date: r.date, jsDate: r.jsDate, value: r.active_techs }));
  const seriesFirstMin = series.map((r) => ({ date: r.date, jsDate: r.jsDate, value: r.avg_first_min, display: r.avg_first_time }));
  const seriesGap = series.map((r) => ({ date: r.date, jsDate: r.jsDate, value: r.avg_gap_min, display: r.avg_gap_display }));

  // Compute a structured delta vs the 30-weekday baseline (excluding today).
  // The pill renders the arrow + colour from `direction`; the formatter
  // converts the absolute diff into a human-readable label like "92 units"
  // or "9m". `null` means "no comparison possible".
  const deltaInfo = (current, base, formatter) => {
    if (current == null || base == null) return null;
    const diff = current - base;
    if (Math.abs(diff) < 0.5) {
      return { direction: 'flat', text: 'on the 30d weekday avg' };
    }
    const absLabel = formatter ? formatter(Math.abs(diff)) : String(Math.abs(Math.round(diff)));
    const direction = diff > 0 ? 'up' : 'down';
    return { direction, text: `${absLabel} ${direction === 'up' ? 'above' : 'below'} 30d weekday avg` };
  };

  // --- presentation ---
  const shortSelectedLabel = new Date(`${selectedDate}T00:00:00`).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
  // Pill label suffix — "today" reads naturally for the default state;
  // any other date gets the short label so the metric is unambiguous.
  const whenSuffix = isViewingToday ? 'today' : shortSelectedLabel;

  return (
    <div className="maintenance-home" style={{ background: C.bg, minHeight: '100vh', padding: '1.5rem 2rem 4rem', fontFamily: sharedFont, color: C.ink }}>
      <div style={{ maxWidth: 1320, margin: '0 auto' }}>
        {/* In-suite overview — the date picker continues to drive the metrics. */}
        <div style={{ marginBottom: 22 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              flexWrap: 'wrap',
              marginBottom: 6,
            }}
          >
            <h1
              style={{
                margin: 0,
                fontSize: 32,
                fontWeight: 700,
                color: C.ink,
                letterSpacing: '-0.02em',
              }}
            >
              In-Suite Maintenance
            </h1>
            {/* Date picker — sits in the top-right of the header so it
                acts like a "viewing as of" control. Lets the back office
                "time travel" the whole dashboard; pills, appointments,
                and upcoming list all re-derive off this date. */}
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                background: C.card,
                border: `1px solid ${C.line}`,
                borderRadius: 8,
                padding: '4px 8px',
                boxShadow: C.shadow,
                marginLeft: 'auto',
              }}
            >
              <FaCalendarAlt style={{ color: C.navy, fontSize: 12 }} />
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value || todayISO())}
                style={{
                  border: 'none',
                  outline: 'none',
                  background: 'transparent',
                  font: 'inherit',
                  color: C.ink,
                  fontSize: 13,
                  fontWeight: 600,
                  padding: 0,
                  cursor: 'pointer',
                }}
              />
              {!isViewingToday && (
                <button
                  type="button"
                  onClick={() => setSelectedDate(todayISO())}
                  style={{
                    background: C.navy,
                    color: '#fff',
                    border: 'none',
                    borderRadius: 6,
                    padding: '3px 10px',
                    fontSize: 11,
                    fontWeight: 700,
                    cursor: 'pointer',
                    letterSpacing: '.02em',
                  }}
                  title="Jump back to today"
                >
                  TODAY
                </button>
              )}
            </div>
          </div>
          <div style={{ color: C.muted, fontSize: 14, marginTop: 4 }}>
            Daily activity, team performance, and maintenance schedules.
          </div>
        </div>

        {/* Today's metric pills — each one carries a 30-day sparkline so
            you can read today's number in context (e.g. "62 units, well
            above the 30-day average of 41"). */}
        <div className="maintenance-home__metrics"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
            gap: 14,
            marginBottom: 18,
          }}
        >
          <ActionPill
            label={`Units completed ${whenSuffix}`}
            value={techTodayLoading ? '—' : (selectedRow.unit_count ?? 0).toLocaleString()}
            delta={
              techTodayLoading
                ? null
                : deltaInfo(
                    selectedRow.unit_count,
                    baseline.unit_count,
                    (d) => `${Math.round(d)} unit${Math.round(d) === 1 ? '' : 's'}`,
                  )
            }
            fallbackHint={
              techTodayLoading
                ? 'Loading…'
                : baseline.unit_count != null
                  ? `30d weekday avg · ${Math.round(baseline.unit_count)}/day`
                  : 'No prior 30d data'
            }
            icon={<FaCheckSquare />}
            accent={C.navy}
            series={seriesUnits}
            loading={techTodayLoading}
            valueFormat={(v) => (v == null ? '—' : `${Math.round(v).toLocaleString()} unit${Math.round(v) === 1 ? '' : 's'}`)}
          />
          <ActionPill
            label={`Active techs ${whenSuffix}`}
            value={techTodayLoading ? '—' : (selectedRow.active_techs ?? 0)}
            delta={
              techTodayLoading
                ? null
                : deltaInfo(
                    selectedRow.active_techs,
                    baseline.active_techs,
                    (d) => `${d.toFixed(1)} tech${Math.round(d) === 1 ? '' : 's'}`,
                  )
            }
            fallbackHint={
              techTodayLoading
                ? 'Loading…'
                : baseline.active_techs != null
                  ? `30d weekday avg · ${baseline.active_techs.toFixed(1)}/day`
                  : 'No prior 30d data'
            }
            icon={<FaUsers />}
            accent={C.purple}
            series={seriesTechs}
            loading={techTodayLoading}
            valueFormat={(v) => (v == null ? '—' : `${Math.round(v)} tech${Math.round(v) === 1 ? '' : 's'}`)}
          />
          <ActionPill
            label={`Avg start time ${whenSuffix}`}
            value={
              techTodayLoading
                ? '—'
                : selectedRow.avg_first_time
                  || (selectedRow.avg_first_min != null ? fmt12h(selectedRow.avg_first_min) : '—')
            }
            delta={
              techTodayLoading
                ? null
                : selectedRow.avg_first_min == null
                  ? null
                  : deltaInfo(
                      selectedRow.avg_first_min,
                      baseline.avg_first_min,
                      (d) => fmtMins(d),
                    )
            }
            fallbackHint={
              techTodayLoading
                ? 'Loading…'
                : selectedRow.avg_first_min == null
                  ? (isViewingToday ? 'No uploads yet today' : 'No uploads on this date')
                  : baseline.avg_first_min != null
                    ? `30d weekday avg · ${fmt12h(baseline.avg_first_min)}`
                    : 'Target ≤ 9:15 AM'
            }
            icon={<FaClock />}
            accent={C.blue}
            series={seriesFirstMin}
            loading={techTodayLoading}
            valueFormat={(v, row) => (v == null ? '—' : (row?.display || fmt12h(v)))}
            referenceLine={{ value: TARGET_MIN, label: 'Target 9:15 AM' }}
          />
          <ActionPill
            label={`Avg time per unit ${whenSuffix}`}
            value={
              techTodayLoading
                ? '—'
                : selectedRow.avg_gap_display
                  || (selectedRow.avg_gap_min != null ? fmtMins(selectedRow.avg_gap_min) : '—')
            }
            delta={
              techTodayLoading
                ? null
                : selectedRow.avg_gap_min == null
                  ? null
                  : deltaInfo(
                      selectedRow.avg_gap_min,
                      baseline.avg_gap_min,
                      (d) => fmtMins(d),
                    )
            }
            fallbackHint={
              techTodayLoading
                ? 'Loading…'
                : selectedRow.avg_gap_min == null
                  ? 'Need ≥ 2 uploads per tech'
                  : baseline.avg_gap_min != null
                    ? `30d weekday avg · ${fmtMins(baseline.avg_gap_min)}`
                    : 'Pace between uploads'
            }
            icon={<FaTachometerAlt />}
            accent={C.green}
            series={seriesGap}
            loading={techTodayLoading}
            valueFormat={(v, row) => (v == null ? '—' : (row?.display || fmtMins(v)))}
          />
        </div>

        {/* Slim navigation strip — the four pills above already show today's
            numbers, so this band just gives a quick jump-off to Analytics
            without taking up another row of vertical space. */}
        <div
          style={{
            background: C.card,
            border: `1px solid ${C.line}`,
            borderRadius: 12,
            padding: '10px 18px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            marginBottom: 18,
            boxShadow: C.shadow,
            flexWrap: 'wrap',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: C.inkSoft, fontSize: 13 }}>
            <FaBolt style={{ color: C.amber }} />
            <span>
              Last 90 days · weekdays only ·{' '}
              <strong style={{ color: C.ink }}>
                {series.reduce((s, r) => s + (r.unit_count || 0), 0).toLocaleString()}
              </strong>{' '}
              units inspected
            </span>
          </div>
          {canViewTab('analytics') && <button
            type="button"
            onClick={() => navigate('/maintenance/analytics')}
            style={{
              background: 'transparent',
              border: 'none',
              color: C.navy,
              fontWeight: 600,
              fontSize: 13,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              cursor: 'pointer',
            }}
          >
            See team performance <FaChevronRight size={10} />
          </button>}
        </div>

        {/* Two-column row */}
        <div className="maintenance-home__panels"
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)',
            gap: 18,
          }}
        >
          {/* Active Maintenances Today — all currently-Active inspections
              across every building, oldest startTerm first. */}
          <div style={sectionStyle}>
            <div style={sectionHeadStyle}>
              <div style={sectionTitleStyle}>
                <FaCalendarAlt style={{ color: C.navy }} />
                Active Maintenances Today
              </div>
              <span style={sectionMetaStyle}>
                {buildingsLoading
                  ? 'Loading…'
                  : `${todaysInspections.length} active maintenance${todaysInspections.length === 1 ? '' : 's'}`}
              </span>
            </div>
            <div>
              {buildingsLoading ? (
                <div style={{ padding: 24, color: C.muted, fontSize: 14 }}>Loading buildings…</div>
              ) : todaysInspections.length === 0 ? (
                <div style={{ padding: '34px 18px', textAlign: 'center', color: C.muted, fontSize: 14 }}>
                  <FaCheckCircle style={{ color: C.green, fontSize: 22, marginBottom: 8 }} />
                  <div style={{ fontWeight: 600, color: C.inkSoft, marginBottom: 4 }}>
                    No active maintenances right now
                  </div>
                  <div>Use Schedule or Calendar to plan upcoming work.</div>
                </div>
              ) : (
                <div>
                  {todaysInspections.map(({ building, inspection }, idx) => {
                    const c = getContractTypeColors(inspection.maintenanceType || building.contractType);
                    const scopeLabel = maintenanceScopeLabel(inspection, building, building.contractType || 'Maintenance');
                    const editUrl = buildEditUrl(building.objectId, inspection.maintenanceID);
                    const isCompleted =
                      inspection.status === 'Past' ||
                      (inspection.objectId && completedInspIds.has(inspection.objectId));
                    // Overdue = the scheduled end date is strictly before
                    // today and the cycle is still Active (not completed).
                    // We compare ISO strings since both sides are YYYY-MM-DD
                    // in the building's local time.
                    const isOverdue =
                      !isCompleted &&
                      !!inspection.endTerm &&
                      inspection.endTerm < today;
                    return (
                      <div className="maintenance-home__visit"
                        key={inspection.objectId || `${building.buildingId}-${inspection.maintenanceID || ''}-${inspection.maintenanceType || ''}-${idx}`}
                        onClick={() => setDetailCtx({ building, inspection })}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setDetailCtx({ building, inspection });
                          }
                        }}
                        title="View this visit's details (dates, daily schedule, filter info, special notes)"
                        // Hover state lives on the row itself so the action
                        // buttons on the right can still show their own
                        // focus / hover treatments without interference.
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f9fafb'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 12,
                          padding: '14px 18px',
                          borderBottom: idx === todaysInspections.length - 1 ? 'none' : `1px solid ${C.line}`,
                          cursor: 'pointer',
                          backgroundColor: 'transparent',
                          transition: 'background-color 120ms ease',
                        }}
                      >
                        <div
                          style={{
                            width: 38,
                            height: 38,
                            borderRadius: 10,
                            background: c.bg,
                            color: c.fg,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          <FaBuilding />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          {/* Title row — building name with the type chip
                              inline to its right, so the scan reads
                              "<building> · <type>" instead of separating
                              them with whitespace. */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                            <div
                              style={{
                                fontWeight: 600,
                                color: C.ink,
                                fontSize: 14,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                minWidth: 0,
                              }}
                              title={building.name || 'Unnamed building'}
                            >
                              {building.name || 'Unnamed building'}
                            </div>
                            {/* Overdue indicator — small red dot directly
                                to the right of the building name when the
                                cycle's end date is in the past. */}
                            {isOverdue && (
                              <span
                                aria-label="Past due"
                                title={`Past due — end date was ${inspection.endTerm}`}
                                style={{
                                  width: 9,
                                  height: 9,
                                  borderRadius: '50%',
                                  background: '#dc2626',
                                  boxShadow: '0 0 0 2px rgba(220,38,38,0.18)',
                                  flexShrink: 0,
                                }}
                              />
                            )}
                            <span
                              style={{
                                display: 'inline-block',
                                padding: '2px 8px',
                                borderRadius: 999,
                                fontSize: 11,
                                fontWeight: 600,
                                background: c.bg,
                                color: c.fg,
                                whiteSpace: 'normal',
                                overflowWrap: 'anywhere',
                                maxWidth: '100%',
                                flexShrink: 1,
                              }}
                              title={scopeLabel}
                            >
                              {scopeLabel}
                            </span>
                          </div>
                          <div style={{ fontSize: 12, color: C.muted, marginTop: 2, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                            {inspection.maintenanceID ? (
                              <span
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  padding: '1px 6px',
                                  borderRadius: 4,
                                  background: '#eef2ff',
                                  color: '#1e40af',
                                  fontSize: 10.5,
                                  fontWeight: 700,
                                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                                  letterSpacing: 0.2,
                                }}
                                title="Maintenance ID — this is the row on Back4App we'll write reports/units to"
                              >
                                {inspection.maintenanceID}
                              </span>
                            ) : null}
                            <span>
                              {fmtRange(inspection.startTerm, inspection.endTerm)}
                              {isCompleted ? ' • Completed' : ''}
                              {building.units ? ` · ${building.units} units` : ''}
                            </span>
                          </div>
                        </div>
                        <div className="maintenance-home__visit-actions" style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                          {/* Edit — jumps to the technician's schedule run for
                              this exact maintenanceID. Opens in a new tab so
                              the hub stays put behind it. Disabled if we
                              don't have a building objectId. */}
                          <button
                            type="button"
                            onClick={(e) => {
                              // The whole row is now a click target that
                              // opens the detail modal, so each action
                              // button stops propagation to keep its
                              // own behaviour distinct.
                              e.stopPropagation();
                              if (editUrl) window.open(editUrl, '_blank', 'noopener,noreferrer');
                            }}
                            disabled={!editUrl}
                            title="Edit the schedule / cycle data for this maintenance (opens in a new tab)"
                            style={{
                              background: '#fff',
                              border: `1px solid ${C.line}`,
                              color: editUrl ? C.inkSoft : C.muted,
                              padding: '6px 10px',
                              borderRadius: 8,
                              fontSize: 12.5,
                              fontWeight: 600,
                              cursor: editUrl ? 'pointer' : 'not-allowed',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 6,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            <FaPen size={10} /> Edit
                          </button>
                          {/* Manager View — same destination as before, just
                              renamed so it sits next to its companion actions. */}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              openBuildingPreview(building.buildingId);
                            }}
                            title="Open the manager's dashboard for this building"
                            style={{
                              background: '#fff',
                              border: `1px solid ${C.line}`,
                              color: C.navy,
                              padding: '6px 10px',
                              borderRadius: 8,
                              fontSize: 12.5,
                              fontWeight: 600,
                              cursor: 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 6,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            Manager View <FaExternalLinkAlt size={9} />
                          </button>
                          {/* Complete — opens an upload modal that attaches
                              the final-report PDF to the right column on the
                              MaintenanceInspections row (finalReport for
                              fancoil/heatpump, dryerReport for dryer vent). */}
                          <button
                            type="button"
                            disabled={!inspection.objectId || isCompleted}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!inspection.objectId) return;
                              setUploadCtx({
                                building,
                                inspection,
                                fieldName: reportFieldForCycle(inspection, building),
                              });
                            }}
                            title={
                              !inspection.objectId
                                ? 'Cannot complete — inspection id missing'
                                : isCompleted
                                ? 'Already marked complete this session'
                                : 'Upload the final report PDF and mark this cycle as complete'
                            }
                            style={{
                              background: isCompleted ? C.greenSoft : C.green,
                              border: `1px solid ${isCompleted ? C.green : C.green}`,
                              color: isCompleted ? C.green : '#fff',
                              padding: '6px 12px',
                              borderRadius: 8,
                              fontSize: 12.5,
                              fontWeight: 700,
                              cursor: inspection.objectId && !isCompleted ? 'pointer' : 'not-allowed',
                              opacity: inspection.objectId && !isCompleted ? 1 : 0.85,
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 6,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            <FaCheckCircle size={12} /> {isCompleted ? 'Completed' : 'Complete'}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Upcoming maintenances */}
          <div style={sectionStyle}>
            <div style={sectionHeadStyle}>
              <div style={sectionTitleStyle}>
                <FaClock style={{ color: C.blue }} />
                Upcoming maintenances
              </div>
              <span style={sectionMetaStyle}>
                {buildingsLoading ? 'Loading…' : 'Next 8'}
              </span>
            </div>
            <div>
              {buildingsLoading ? (
                <div style={{ padding: 24, color: C.muted, fontSize: 14 }}>Loading…</div>
              ) : upcomingMaintenances.length === 0 ? (
                <div style={{ padding: '24px 16px', textAlign: 'center', color: C.muted, fontSize: 14 }}>
                  <FaCheckCircle style={{ color: C.green, fontSize: 18, marginBottom: 6 }} />
                  <div>Nothing scheduled in the next 60 days.</div>
                </div>
              ) : (
                <div>
                  {upcomingMaintenances.slice(0, 8).map((row, i, all) => {
                    const { building, inspection, days } = row;
                    const isSoon = days <= 3;
                    return (
                      <button
                        type="button"
                        key={`${building.buildingId}-${inspection.maintenanceID || inspection.startTerm}-${i}`}
                        onClick={() => setDetailCtx({ building, inspection })}
                        title="View this visit's details (dates, daily schedule, filter info, special notes)"
                        style={{
                          all: 'unset',
                          display: 'block',
                          width: '100%',
                          boxSizing: 'border-box',
                          padding: '12px 18px',
                          borderBottom: i === all.slice(0, 8).length - 1 ? 'none' : `1px solid ${C.line}`,
                          cursor: 'pointer',
                          transition: 'background-color 120ms',
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#fafbfd')}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '')}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div
                            style={{
                              minWidth: 64,
                              padding: '4px 9px',
                              borderRadius: 8,
                              background: isSoon ? C.amberSoft : C.blueSoft,
                              color: isSoon ? C.amberInk : C.blueInk,
                              fontSize: 11,
                              fontWeight: 700,
                              textAlign: 'center',
                              flexShrink: 0,
                            }}
                          >
                            {fmtDaysUntil(days)}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div
                              style={{
                                fontWeight: 600,
                                color: C.ink,
                                fontSize: 13,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {building.name || 'Unnamed building'}
                            </div>
                            <div
                              style={{
                                fontSize: 11.5,
                                color: C.muted,
                                fontWeight: 500,
                                marginTop: 2,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 6,
                                flexWrap: 'wrap',
                              }}
                            >
                              {inspection.maintenanceID ? (
                                <span
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    padding: '1px 6px',
                                    borderRadius: 4,
                                    background: '#eef2ff',
                                    color: '#1e40af',
                                    fontSize: 10,
                                    fontWeight: 700,
                                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                                    letterSpacing: 0.2,
                                  }}
                                  title="Maintenance ID"
                                >
                                  {inspection.maintenanceID}
                                </span>
                              ) : null}
                              <span>
                                {maintenanceScopeLabel(inspection, building, building.contractType || 'Maintenance')}
                                {inspection.startTerm ? ` · ${fmtRange(inspection.startTerm, inspection.endTerm)}` : ''}
                              </span>
                            </div>
                            {(building.filterType || building.filterSize) ? (
                              <div style={{ fontSize: 11, color: C.muted, marginTop: 3, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                <FaFilter style={{ fontSize: 9 }} />
                                {[building.filterType, building.filterSize].filter(Boolean).join(' · ')}
                              </div>
                            ) : null}
                            {inspection.notes ? (
                              <div
                                style={{
                                  fontSize: 11,
                                  color: C.amberInk,
                                  marginTop: 3,
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 4,
                                  maxWidth: '100%',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}
                                title={inspection.notes}
                              >
                                <FaStickyNote style={{ fontSize: 9, color: C.amber, flexShrink: 0 }} />
                                {inspection.notes}
                              </div>
                            ) : null}
                          </div>
                          <FaChevronRight style={{ color: C.muted, fontSize: 11, flexShrink: 0 }} />
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Quick links */}
        {['schedule', 'calendar', 'analytics'].some(canViewTab) && <div style={{ marginTop: 22 }}>
          <div style={{ ...sectionTitleStyle, marginBottom: 10, fontSize: 12.5, color: C.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em' }}>
            Jump to
          </div>
          <div className="maintenance-home__quick-links"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
              gap: 12,
            }}
          >
            {canViewTab('schedule') && <ActionCard
              icon={<FaCalendarAlt />}
              accent={C.navy}
              title="Schedule"
              body="Plan and review every contract — what's overdue, what's due, what's done."
              onClick={() => navigate('/maintenance/schedule')}
            />}
            {canViewTab('calendar') && <ActionCard
              icon={<FaClock />}
              accent={C.amber}
              title="Calendar"
              body="Month and week views of every inspection window across all buildings."
              onClick={() => navigate('/maintenance/calendar')}
            />}
            {canViewTab('analytics') && <ActionCard
              icon={<FaChartLine />}
              accent={C.purple}
              title="Performance"
              body="Team punctuality, pace per unit, and per-technician performance."
              onClick={() => navigate('/maintenance/analytics')}
            />}
          </div>
        </div>}
      </div>
      {uploadCtx ? (
        <UploadReportModal
          ctx={uploadCtx}
          onClose={() => setUploadCtx(null)}
          onCompleted={(inspectionId) => {
            setCompletedInspIds((prev) => {
              const next = new Set(prev);
              if (inspectionId) next.add(inspectionId);
              return next;
            });
            setUploadCtx(null);
          }}
        />
      ) : null}
      {detailCtx ? (
        <MaintenanceModal
          inspection={detailCtx.inspection}
          building={detailCtx.building}
          onClose={() => setDetailCtx(null)}
          // After an edit, re-fetch buildings so the upcoming list / row
          // metadata reflect the new dates / notes / filter info.
          onSaved={() => { reloadBuildings(); }}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Final-report upload modal
// ---------------------------------------------------------------------------
// Single-purpose modal: pick a PDF, POST it to /api/maintenance-inspections/
// <id>/upload-report (multipart), and tell the parent when the cycle is
// successfully marked complete so it can grey out the row.

function UploadReportModal({ ctx, onClose, onCompleted }) {
  const { building, inspection, fieldName } = ctx;
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  // Two-step flow: 'upload' (default) → 'success'. We don't tell the
  // parent the cycle is Completed (which greys out the row) until the
  // user actually dismisses the success view — that way they have time
  // to click "View Manager Dashboard" while the row is still visible.
  const [step, setStep] = useState('upload');
  const dropRef = React.useRef(null);

  const fieldLabel = fieldName === 'dryerReport' ? 'Dryer Vent Report' : 'Final Report';
  const acceptedHint = 'PDF or Excel (.xlsx, .xls) · max 25 MB';

  // Allowed types — keep frontend + server in sync. .xls is the legacy
  // binary Excel format, .xlsx is the modern zip-based one. Some browsers
  // (notably Safari) report Excel files with a generic
  // application/octet-stream mimetype, so we accept that ONLY when the
  // filename has the right extension.
  const ACCEPTED_EXTENSIONS = ['.pdf', '.xlsx', '.xls'];
  const ACCEPTED_MIMES = new Set([
    'application/pdf',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ]);

  const handleFile = (f) => {
    setError('');
    if (!f) return;
    const lower = (f.name || '').toLowerCase();
    const extOk = ACCEPTED_EXTENSIONS.some((e) => lower.endsWith(e));
    const mimeOk = ACCEPTED_MIMES.has(f.type);
    if (!extOk && !mimeOk) {
      setError('That file isn\'t a PDF or Excel. Allowed: .pdf, .xlsx, .xls.');
      return;
    }
    const MAX = 25 * 1024 * 1024;
    if (f.size > MAX) {
      setError('File is over 25 MB — compress it first.');
      return;
    }
    setFile(f);
  };

  const onSubmit = async () => {
    if (!file || uploading) return;
    setUploading(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('fieldName', fieldName);
      const r = await fetch(
        `${API_BASE_URL}/api/maintenance-inspections/${encodeURIComponent(inspection.objectId)}/upload-report`,
        { method: 'POST', body: fd },
      );
      const body = await r.json().catch(() => ({}));
      if (!r.ok || body.success === false) {
        const msg = body.error || `Upload failed (${r.status})`;
        setError(`${msg}${body.detail ? ` — ${JSON.stringify(body.detail).slice(0, 160)}` : ''}`);
        return;
      }
      // Move to the post-upload success view. Notifying the parent
      // (which removes the row from the active list) is deferred until
      // the user clicks Done / View Manager Dashboard so the row stays
      // visible behind the success modal in case they want to refer
      // back to it.
      setStep('success');
    } catch (e) {
      setError(`Network error: ${e.message || e}`);
    } finally {
      setUploading(false);
    }
  };

  // Finish the flow — mark the row Completed and close. Used by both
  // success-step buttons; the "View Manager Dashboard" CTA also opens
  // the preview tab first, then calls this.
  const finish = () => {
    onCompleted(inspection.objectId);
  };

  const handleViewManagerDashboard = () => {
    openBuildingPreview(building.buildingId);
    finish();
  };

  // Click-outside / Esc to dismiss when not actively uploading. On the
  // success step a dismiss still counts as "done" so the row updates.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape' || uploading) return;
      if (step === 'success') finish();
      else onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploading, step]);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 20,
      }}
      onClick={(e) => {
        if (e.target !== e.currentTarget || uploading) return;
        // Backdrop click on the success step still finalises the row
        // — same as the explicit "Done" button.
        if (step === 'success') finish();
        else onClose();
      }}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 14,
          width: '100%',
          maxWidth: 520,
          boxShadow: '0 20px 60px rgba(15,23,42,.25)',
          overflow: 'hidden',
        }}
      >
        <div style={{ padding: '18px 22px', borderBottom: `1px solid ${C.line}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: C.ink }}>
              {step === 'success' ? `${fieldLabel} uploaded` : `Upload ${fieldLabel}`}
            </div>
            <div style={{ fontSize: 12.5, color: C.muted, marginTop: 3 }}>
              {building.name || 'Building'}
              {inspection.maintenanceID ? ` · cycle ${inspection.maintenanceID}` : ''}
              {inspection.maintenanceType ? ` · ${maintenanceScopeLabel(inspection, building)}` : ''}
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              if (uploading) return;
              if (step === 'success') finish();
              else onClose();
            }}
            aria-label="Close"
            style={{ background: 'transparent', border: 'none', cursor: uploading ? 'not-allowed' : 'pointer', color: C.muted, padding: 4 }}
          >
            <FaTimes />
          </button>
        </div>

        {step === 'upload' && (
        <div style={{ padding: 22 }}>
          <div
            ref={dropRef}
            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
            onDrop={(e) => { e.preventDefault(); e.stopPropagation(); const f = e.dataTransfer.files?.[0]; if (f) handleFile(f); }}
            style={{
              border: `2px dashed ${file ? C.green : C.line}`,
              borderRadius: 12,
              padding: 26,
              textAlign: 'center',
              background: file ? C.greenSoft : C.bg,
              cursor: 'pointer',
              transition: 'background 120ms ease, border-color 120ms ease',
            }}
            onClick={() => document.getElementById('report-upload-input')?.click()}
          >
            {/* Pick the matching icon for the currently-staged file — Excel
                files get the green spreadsheet glyph, everything else the
                red PDF glyph. With no file selected yet we default to the
                PDF icon (most common case). */}
            {file && /\.(xlsx|xls)$/i.test(file.name) ? (
              <FaFileExcel size={28} style={{ color: C.green, marginBottom: 8 }} />
            ) : (
              <FaFilePdf size={28} style={{ color: file ? C.green : C.navy, marginBottom: 8 }} />
            )}
            <div style={{ fontSize: 14, fontWeight: 600, color: C.ink }}>
              {file ? file.name : 'Drop a PDF or Excel here or click to browse'}
            </div>
            <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>
              {file ? `${(file.size / 1024 / 1024).toFixed(2)} MB · saved as ${fieldName}` : acceptedHint}
            </div>
            <input
              id="report-upload-input"
              type="file"
              accept="application/pdf,.pdf,application/vnd.ms-excel,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.xlsx"
              style={{ display: 'none' }}
              onChange={(e) => handleFile(e.target.files?.[0])}
            />
          </div>

          {error ? (
            <div style={{ marginTop: 12, padding: '10px 12px', background: C.redSoft, color: C.redInk, borderRadius: 8, fontSize: 13 }}>
              {error}
            </div>
          ) : null}

          <div style={{ marginTop: 16, fontSize: 12, color: C.muted, lineHeight: 1.45 }}>
            We'll write this file to the <code style={{ background: '#eef2ff', padding: '1px 6px', borderRadius: 4 }}>{fieldName}</code> column on the MaintenanceInspections row for this cycle. The cycle will start showing as Completed across the dashboard.
          </div>
        </div>
        )}

        {step === 'upload' && (
        <div style={{ padding: '14px 22px', borderTop: `1px solid ${C.line}`, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10, background: '#fafbfc' }}>
          <button
            type="button"
            onClick={() => !uploading && onClose()}
            disabled={uploading}
            style={{ background: '#fff', border: `1px solid ${C.line}`, color: C.inkSoft, padding: '8px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: uploading ? 'not-allowed' : 'pointer' }}
          >Cancel</button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={!file || uploading}
            style={{
              background: !file || uploading ? '#94a3b8' : C.green,
              border: 'none',
              color: '#fff',
              padding: '8px 16px',
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 700,
              cursor: !file || uploading ? 'not-allowed' : 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            {uploading ? (<><FaSpinner className="spin" /> Uploading…</>) : (<><FaCloudUploadAlt /> Upload &amp; complete</>)}
          </button>
        </div>
        )}

        {step === 'success' && (
          <>
            <div style={{ padding: '24px 22px 18px', textAlign: 'center' }}>
              <div
                style={{
                  width: 60,
                  height: 60,
                  margin: '0 auto 12px',
                  borderRadius: '50%',
                  background: C.greenSoft,
                  color: C.green,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 28,
                }}
                aria-hidden
              >
                <FaCheckCircle />
              </div>
              <div style={{ fontSize: 17, fontWeight: 700, color: C.ink, marginBottom: 4 }}>
                {fieldLabel} saved
              </div>
              <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.5, maxWidth: 380, margin: '0 auto' }}>
                The cycle is now marked Completed. Want to see how it looks to the manager?
              </div>
            </div>
            <div
              style={{
                padding: '14px 22px',
                borderTop: `1px solid ${C.line}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 10,
                background: '#fafbfc',
                flexWrap: 'wrap',
              }}
            >
              <button
                type="button"
                onClick={finish}
                style={{ background: '#fff', border: `1px solid ${C.line}`, color: C.inkSoft, padding: '8px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
              >
                Done
              </button>
              <button
                type="button"
                onClick={handleViewManagerDashboard}
                title="Open the manager's dashboard for this building in a new tab (Previewing as manager)"
                style={{
                  background: C.navy,
                  border: 'none',
                  color: '#fff',
                  padding: '8px 16px',
                  borderRadius: 8,
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                View Manager Dashboard <FaExternalLinkAlt size={11} />
              </button>
            </div>
          </>
        )}
      </div>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } } .spin { animation: spin 0.9s linear infinite; }`}</style>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function ActionPill({
  label,
  value,
  delta,            // { direction: 'up' | 'down' | 'flat', text: string } | null
  fallbackHint,     // shown when `delta` is null (e.g. no comparison yet)
  icon,
  accent,
  onClick,
  clickable,
  series,
  loading,
  valueFormat,
  referenceLine,
}) {
  const Tag = clickable ? 'button' : 'div';
  let arrow = null;
  let subtitle = fallbackHint;
  if (delta) {
    subtitle = delta.text;
    if (delta.direction === 'up') {
      arrow = <span style={{ color: C.green, marginRight: 5, fontSize: 11 }}>▲</span>;
    } else if (delta.direction === 'down') {
      arrow = <span style={{ color: C.red, marginRight: 5, fontSize: 11 }}>▼</span>;
    } else if (delta.direction === 'flat') {
      arrow = <span style={{ color: C.muted, marginRight: 5, fontSize: 11 }}>—</span>;
    }
  }
  return (
    <Tag
      type={clickable ? 'button' : undefined}
      onClick={onClick}
      style={{
        background: C.card,
        border: `1px solid ${C.line}`,
        borderRadius: 12,
        padding: '16px 18px',
        boxShadow: C.shadow,
        textAlign: 'left',
        cursor: clickable ? 'pointer' : 'default',
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        font: 'inherit',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ fontSize: 11.5, textTransform: 'uppercase', letterSpacing: '.06em', color: C.muted, fontWeight: 700 }}>
          {label}
        </div>
        <span style={{ color: accent, fontSize: 14 }}>{icon}</span>
      </div>
      <div
        style={{
          fontSize: 30,
          fontWeight: 700,
          // Per design feedback, the headline number is always black so it
          // reads as a fact; colour signalling lives in the arrow below.
          color: C.ink,
          letterSpacing: '-0.02em',
          lineHeight: 1.05,
        }}
      >
        {value}
      </div>
      <div style={{ fontSize: 12, color: C.muted, display: 'flex', alignItems: 'center' }}>
        {arrow}
        <span>{subtitle}</span>
      </div>
      {Array.isArray(series) && series.length > 1 ? (
        <Sparkline
          data={series}
          loading={loading}
          valueFormat={valueFormat}
          referenceLine={referenceLine}
        />
      ) : null}
    </Tag>
  );
}

// 90-day weekday-only line chart for the home page pills. Renders:
//   • a 1.5px blue line + 12%-opacity blue area underneath
//   • a dot on the most recent (today) value
//   • month tick labels under the chart
//   • an optional dashed reference line (used for the 9:15 AM target on
//     the avg-start-time pill)
//   • a hover tooltip that follows the mouse and shows the date + value
//   • tiny min/max range labels in the top-right of the chart
//
// `data` items are `{ date, jsDate, value, display? }`. `valueFormat` is
// a function `(value, row) => string` used by the tooltip.
function Sparkline({ data, loading, valueFormat, referenceLine }) {
  const svgRef = React.useRef(null);
  const [hover, setHover] = React.useState(null); // { idx, x, y }

  const W = 280;
  const H = 60;
  const padX = 4;
  const padTop = 6;
  const padBottom = 6;
  const innerH = H - padTop - padBottom;

  const points = useMemo(() => data.map((d, i) => ({
    idx: i,
    date: d.date,
    jsDate: d.jsDate instanceof Date ? d.jsDate : (d.date ? new Date(d.date) : null),
    value: d.value == null || Number.isNaN(Number(d.value)) ? null : Number(d.value),
    display: d.display,
  })), [data]);

  const nonNull = points.filter((p) => p.value != null);

  if (loading) {
    return (
      <div
        style={{
          marginTop: 10,
          height: H + 16,
          background: 'linear-gradient(90deg, #eef3fa 0%, #f8fbff 50%, #eef3fa 100%)',
          borderRadius: 6,
        }}
      />
    );
  }
  if (nonNull.length < 2) {
    return (
      <div
        style={{
          marginTop: 10,
          height: H + 16,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: C.muted,
          fontSize: 11,
        }}
      >Not enough data yet</div>
    );
  }

  // Y axis covers the data range. Include the reference line in the
  // range if it's outside the data, so the dashed line is always visible.
  let yMin = Math.min(...nonNull.map((p) => p.value));
  let yMax = Math.max(...nonNull.map((p) => p.value));
  if (referenceLine && Number.isFinite(referenceLine.value)) {
    yMin = Math.min(yMin, referenceLine.value);
    yMax = Math.max(yMax, referenceLine.value);
  }
  // Add 5% breathing room so points don't sit on the top/bottom edge.
  const pad = (yMax - yMin) * 0.08;
  if (pad > 0) { yMin -= pad; yMax += pad; }
  const yRange = yMax - yMin || 1;

  const xStep = (W - padX * 2) / Math.max(1, points.length - 1);
  const projectY = (v) => padTop + (1 - (v - yMin) / yRange) * innerH;
  const projectX = (i) => padX + i * xStep;

  // ----- line + area paths -----
  let linePath = '';
  let inSegment = false;
  points.forEach((p) => {
    if (p.value == null) { inSegment = false; return; }
    const x = projectX(p.idx);
    const y = projectY(p.value);
    linePath += `${inSegment ? 'L' : 'M'}${x.toFixed(2)},${y.toFixed(2)} `;
    inSegment = true;
  });
  let areaPath = '';
  const areaPts = nonNull.map((p) => ({ x: projectX(p.idx), y: projectY(p.value) }));
  if (areaPts.length >= 2) {
    const first = areaPts[0];
    const last = areaPts[areaPts.length - 1];
    areaPath += `M${first.x.toFixed(2)},${(H - padBottom).toFixed(2)} `;
    areaPts.forEach((pt) => { areaPath += `L${pt.x.toFixed(2)},${pt.y.toFixed(2)} `; });
    areaPath += `L${last.x.toFixed(2)},${(H - padBottom).toFixed(2)} Z`;
  }

  // ----- last point + reference line geometry -----
  const lastIdx = (() => {
    for (let i = points.length - 1; i >= 0; i--) if (points[i].value != null) return i;
    return -1;
  })();
  const lastPoint = lastIdx >= 0 ? {
    x: projectX(lastIdx),
    y: projectY(points[lastIdx].value),
  } : null;
  const refY = referenceLine && Number.isFinite(referenceLine.value) ? projectY(referenceLine.value) : null;

  // ----- x-axis month ticks (one label per month transition) -----
  // Build a list of {idx, label} where the month differs from the prior
  // weekday's month. Always includes the very first point.
  const monthTicks = [];
  let prevMonthKey = null;
  points.forEach((p, i) => {
    if (!p.jsDate) return;
    const key = `${p.jsDate.getFullYear()}-${p.jsDate.getMonth()}`;
    if (key !== prevMonthKey) {
      monthTicks.push({ idx: i, label: p.jsDate.toLocaleDateString('en-US', { month: 'short' }) });
      prevMonthKey = key;
    }
  });

  // ----- hover handling -----
  const handleMove = (e) => {
    const node = svgRef.current;
    if (!node) return;
    const rect = node.getBoundingClientRect();
    const xPct = (e.clientX - rect.left) / rect.width;
    let idx = Math.round(xPct * (points.length - 1));
    if (idx < 0) idx = 0;
    if (idx > points.length - 1) idx = points.length - 1;
    const p = points[idx];
    if (p.value == null) {
      // Fall back to the nearest non-null on either side so the tooltip
      // still has something to show on padded zero-days.
      let left = idx - 1; let right = idx + 1;
      while (left >= 0 || right < points.length) {
        if (left >= 0 && points[left].value != null) { idx = left; break; }
        if (right < points.length && points[right].value != null) { idx = right; break; }
        left--; right++;
      }
    }
    setHover({
      idx,
      xPct: (projectX(idx) - padX) / (W - padX * 2),
    });
  };
  const handleLeave = () => setHover(null);

  const hoverPoint = hover ? points[hover.idx] : null;
  const hoverHasValue = hoverPoint && hoverPoint.value != null;

  // Tooltip placement (CSS %, relative to chart container).
  const tooltipLeftPct = hover ? Math.max(0, Math.min(100, hover.xPct * 100)) : 0;
  const tooltipDate = hoverPoint?.jsDate
    ? hoverPoint.jsDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
    : '';
  const tooltipValue = hoverHasValue
    ? (valueFormat ? valueFormat(hoverPoint.value, hoverPoint) : String(hoverPoint.value))
    : 'No data';

  // Range labels — clamp to short numeric strings so they don't hog space.
  const fmtRange = (v) => {
    if (!Number.isFinite(v)) return '';
    if (Math.abs(v) >= 100) return Math.round(v).toString();
    if (Math.abs(v) >= 10) return v.toFixed(0);
    return v.toFixed(1);
  };

  return (
    <div style={{ marginTop: 10, position: 'relative' }}>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        style={{ width: '100%', height: H, display: 'block', cursor: 'crosshair' }}
        onMouseMove={handleMove}
        onMouseLeave={handleLeave}
      >
        {/* dashed reference line */}
        {refY != null ? (
          <g>
            <line
              x1={padX}
              x2={W - padX}
              y1={refY}
              y2={refY}
              stroke={C.amber}
              strokeWidth={0.75}
              strokeDasharray="3 3"
              opacity={0.85}
            />
          </g>
        ) : null}
        {/* area + line */}
        {areaPath ? <path d={areaPath} fill={C.blue} fillOpacity={0.12} /> : null}
        {linePath ? (
          <path
            d={linePath}
            fill="none"
            stroke={C.blue}
            strokeWidth={1.5}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        ) : null}
        {/* last point */}
        {lastPoint ? (
          <circle
            cx={lastPoint.x}
            cy={lastPoint.y}
            r={2.6}
            fill={C.blue}
            stroke="#fff"
            strokeWidth={1.25}
          />
        ) : null}
        {/* hover indicator */}
        {hoverHasValue ? (
          <g>
            <line
              x1={projectX(hover.idx)}
              x2={projectX(hover.idx)}
              y1={padTop}
              y2={H - padBottom}
              stroke={C.blue}
              strokeWidth={0.6}
              strokeDasharray="2 2"
              opacity={0.55}
            />
            <circle
              cx={projectX(hover.idx)}
              cy={projectY(hoverPoint.value)}
              r={3}
              fill="#fff"
              stroke={C.blue}
              strokeWidth={1.5}
            />
          </g>
        ) : null}
      </svg>
      {/* x-axis month ticks (CSS-positioned so they don't fight with the
          stretched SVG viewBox). Each tick anchors at the chart x % of the
          first data point of that month. */}
      <div style={{ position: 'relative', height: 14, marginTop: 3 }}>
        {monthTicks.map((t) => {
          const pct = (projectX(t.idx) - padX) / (W - padX * 2) * 100;
          return (
            <div
              key={`${t.idx}-${t.label}`}
              style={{
                position: 'absolute',
                left: `${pct}%`,
                transform: 'translateX(-50%)',
                top: 0,
                fontSize: 9.5,
                color: C.muted,
                whiteSpace: 'nowrap',
                fontWeight: 600,
                letterSpacing: '.02em',
              }}
            >{t.label}</div>
          );
        })}
      </div>
      {/* min/max range labels (top-right of chart) */}
      <div
        style={{
          position: 'absolute',
          top: 2,
          right: 4,
          fontSize: 9.5,
          color: C.muted,
          background: 'rgba(255,255,255,.6)',
          padding: '0 4px',
          borderRadius: 4,
          lineHeight: 1.2,
          pointerEvents: 'none',
        }}
        title={referenceLine ? `Range includes target ${referenceLine.label || ''}` : 'Range over the visible window'}
      >
        {fmtRange(yMax)} / {fmtRange(yMin)}
      </div>
      {/* reference line legend */}
      {referenceLine ? (
        <div
          style={{
            position: 'absolute',
            top: 2,
            left: 4,
            fontSize: 9.5,
            color: C.amber,
            background: 'rgba(255,255,255,.6)',
            padding: '0 4px',
            borderRadius: 4,
            lineHeight: 1.2,
            pointerEvents: 'none',
            fontWeight: 600,
          }}
        >― {referenceLine.label || 'target'}</div>
      ) : null}
      {/* tooltip */}
      {hoverHasValue ? (
        <div
          style={{
            position: 'absolute',
            bottom: H + 6,
            left: `${tooltipLeftPct}%`,
            transform: `translateX(${tooltipLeftPct > 80 ? '-100%' : tooltipLeftPct < 20 ? '0%' : '-50%'})`,
            background: '#1a2236',
            color: '#fff',
            padding: '5px 8px',
            borderRadius: 6,
            fontSize: 11,
            lineHeight: 1.3,
            whiteSpace: 'nowrap',
            boxShadow: '0 4px 12px rgba(20,30,60,.18)',
            pointerEvents: 'none',
            zIndex: 5,
            fontWeight: 600,
          }}
        >
          <div style={{ opacity: 0.75, fontWeight: 500, fontSize: 10 }}>{tooltipDate}</div>
          <div>{tooltipValue}</div>
        </div>
      ) : null}
    </div>
  );
}

function ActionCard({ icon, accent, title, body, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        all: 'unset',
        cursor: 'pointer',
        background: C.card,
        border: `1px solid ${C.line}`,
        borderRadius: 12,
        padding: 18,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        boxSizing: 'border-box',
        transition: 'transform 120ms ease, box-shadow 120ms ease, border-color 120ms ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-1px)';
        e.currentTarget.style.boxShadow = C.shadow;
        e.currentTarget.style.borderColor = accent;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = '';
        e.currentTarget.style.boxShadow = '';
        e.currentTarget.style.borderColor = C.line;
      }}
    >
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: 10,
          background: `${accent}14`,
          color: accent,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 16,
        }}
      >
        {icon}
      </div>
      <div>
        <div style={{ fontSize: 15, fontWeight: 700, color: C.ink, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
          {title}
          <FaChevronRight style={{ fontSize: 11, color: C.muted }} />
        </div>
        <div style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.45 }}>{body}</div>
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Shared styles
// ---------------------------------------------------------------------------

const sectionStyle = {
  background: C.card,
  border: `1px solid ${C.line}`,
  borderRadius: 14,
  boxShadow: C.shadow,
  overflow: 'hidden',
};

const sectionHeadStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '14px 18px',
  borderBottom: `1px solid ${C.line}`,
  background: '#fcfdff',
  gap: 12,
  flexWrap: 'wrap',
};

const sectionTitleStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  fontSize: 14,
  fontWeight: 700,
  color: C.ink,
};

const sectionMetaStyle = {
  fontSize: 12,
  color: C.muted,
  fontWeight: 500,
};
